import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { reportCommentsApi } from '@/api/report-comments';
import {
    CreateReportCommentPayload,
    CreateReportCommentReplyPayload,
    ReportComment,
    ReportCommentStatus,
} from '@/types/report-comments';
import { REPORT_COMMENTS_QUERY_KEY } from './use-report-comments';
import { removeReportCommentFromList, upsertReportCommentInList } from '../utils';

type QueryData = ReportComment[];

const getRequiredReportId = (reportId?: string | null) => {
    if (!reportId) {
        throw new Error('Report id is required for comments operations.');
    }

    return reportId;
};

export function useReportCommentMutations(reportId?: string | null) {
    const queryClient = useQueryClient();
    const queryKey = [REPORT_COMMENTS_QUERY_KEY, reportId];

    const updateCachedComments = (updater: (comments: QueryData) => QueryData) => {
        queryClient.setQueryData<QueryData>(queryKey, (current = []) => updater(current));
    };

    const createCommentMutation = useMutation({
        mutationFn: (payload: CreateReportCommentPayload) =>
            reportCommentsApi.create(getRequiredReportId(reportId), payload),
        onSuccess: (comment) => {
            updateCachedComments((comments) => upsertReportCommentInList(comments, comment));
            toast.success('Comentário adicionado com sucesso');
        },
        onError: () => {
            toast.error('Erro ao criar comentário');
        },
    });

    const replyToCommentMutation = useMutation({
        mutationFn: ({
            commentId,
            payload,
        }: {
            commentId: string;
            payload: CreateReportCommentReplyPayload;
        }) => reportCommentsApi.reply(getRequiredReportId(reportId), commentId, payload),
        onSuccess: (comment) => {
            updateCachedComments((comments) => upsertReportCommentInList(comments, comment));
        },
        onError: () => {
            toast.error('Erro ao adicionar complemento');
        },
    });

    const updateCommentStatusMutation = useMutation({
        mutationFn: ({ commentId, status }: { commentId: string; status: ReportCommentStatus }) =>
            reportCommentsApi.updateStatus(getRequiredReportId(reportId), commentId, { status }),
        onSuccess: (comment) => {
            updateCachedComments((comments) => upsertReportCommentInList(comments, comment));
        },
        onError: () => {
            toast.error('Erro ao atualizar comentário');
        },
    });

    const deleteCommentMutation = useMutation({
        mutationFn: (commentId: string) =>
            reportCommentsApi.remove(getRequiredReportId(reportId), commentId),
        onSuccess: (_data, commentId) => {
            updateCachedComments((comments) => removeReportCommentFromList(comments, commentId));
        },
        onError: () => {
            toast.error('Erro ao remover comentário');
        },
    });

    return {
        createCommentMutation,
        replyToCommentMutation,
        updateCommentStatusMutation,
        deleteCommentMutation,
    };
}
